import { GEM_FILES, humanizeGemLabel, isPreciousGem } from "../constants/gems";
import { GAME_LEVEL_COUNT } from "../constants/gameLevels";

const COMMON_GEM_IDS = GEM_FILES.map((filename) =>
  filename.replace(/\.[^.]+$/, "")
).filter((gemId) => !isPreciousGem(gemId));

/** Après autant de jours d'affilée sans lecture, le prochain tirage avec une case A l'inclut. */
export const READING_PITY_AFTER_DAYS = 4;

/** Configurations journalières : répartition A / B / C et poids relatif. */
export const DAILY_QUEST_CONFIGURATIONS = [
  { A: 2, B: 1, C: 0, D: 1, weight: 20 },
  { A: 3, B: 0, C: 0, D: 1, weight: 20 },
  { A: 0, B: 3, C: 0, D: 1, weight: 20 },
  { A: 1, B: 2, C: 0, D: 1, weight: 20 },
  { A: 1, B: 1, C: 1, D: 1, weight: 10 },
  { A: 2, B: 0, C: 1, D: 1, weight: 10 },
];

/**
 * Catégories d'objectifs par groupe.
 * Chaque catégorie a la même probabilité d'être tirée au sein du groupe ;
 * les variantes suivent ensuite leurs poids (pourcentages du tableau).
 */
const QUEST_CATALOG = {
  A: [
    {
      id: "capture",
      variants: [
        { id: "a-capture-1", label: "Capturer 1 mot", weight: 20 },
        { id: "a-capture-2", label: "Capturer 2 mots", weight: 20 },
        { id: "a-capture-3", label: "Capturer 3 mots", weight: 20 },
        { id: "a-capture-4", label: "Capturer 4 mots", weight: 20 },
        { id: "a-capture-5", label: "Capturer 5 mots", weight: 20 },
      ],
    },
    {
      id: "play-games",
      variants: [
        { id: "a-play-2", label: "Jouer 2 parties", weight: 25 },
        { id: "a-play-3", label: "Jouer 3 parties", weight: 25 },
        { id: "a-play-4", label: "Jouer 4 parties", weight: 25 },
        { id: "a-play-5", label: "Jouer 5 parties", weight: 25 },
      ],
    },
    {
      id: "reading",
      variants: [
        { id: "a-read-60", label: "Consacrer 60 minutes à la lecture", weight: 30 },
        { id: "a-read-90", label: "Consacrer 90 minutes à la lecture", weight: 30 },
        { id: "a-read-120", label: "Consacrer 120 minutes à la lecture", weight: 20 },
        { id: "a-read-180", label: "Consacrer 180 minutes à la lecture", weight: 20 },
      ],
    },
    {
      id: "memory-plays",
      variants: [
        { id: "a-memory-1", label: "Jouer le jeu de mémoire au moins 1 fois", weight: 25 },
        { id: "a-memory-2", label: "Jouer le jeu de mémoire au moins 2 fois", weight: 25 },
        { id: "a-memory-3", label: "Jouer le jeu de mémoire au moins 3 fois", weight: 25 },
        { id: "a-memory-5", label: "Jouer le jeu de mémoire au moins 5 fois", weight: 25 },
      ],
    },
  ],
  B: [
    {
      id: "min-score-partie",
      partieScore: true,
      variants: [
        { id: "b-score-12", label: "Obtenir au moins un 12/15 dans une partie", weight: 70 },
        { id: "b-score-13", label: "Obtenir au moins un 13/15 dans une partie", weight: 20 },
        { id: "b-score-14", label: "Obtenir au moins un 14/15 dans une partie", weight: 10 },
      ],
    },
    {
      id: "zamselly-score",
      partieScore: true,
      variants: [
        { id: "b-zamselly-11", label: "Utiliser le parchemin Zamselly et obtenir au moins un 11/15", weight: 40 },
        { id: "b-zamselly-12", label: "Utiliser le parchemin Zamselly et obtenir au moins un 12/15", weight: 40 },
        { id: "b-zamselly-13", label: "Utiliser le parchemin Zamselly et obtenir au moins un 13/15", weight: 20 },
      ],
    },
    {
      id: "melitaea-score",
      partieScore: true,
      variants: [
        { id: "b-melitaea-11", label: "Utiliser le parchemin Melitaea et obtenir au moins un 11/15", weight: 40 },
        { id: "b-melitaea-12", label: "Utiliser le parchemin Melitaea et obtenir au moins un 12/15", weight: 40 },
        { id: "b-melitaea-13", label: "Utiliser le parchemin Melitaea et obtenir au moins un 13/15", weight: 20 },
      ],
    },
    {
      id: "no-potion-key-score",
      partieScore: true,
      variants: [
        {
          id: "b-clean-12",
          label:
            "Dans le niveau actuel, jouer une partie sans potion et sans parchemin et obtenir au moins un 12/15",
          weight: 33.3,
        },
        {
          id: "b-clean-13",
          label:
            "Dans le niveau actuel, jouer une partie sans potion et sans parchemin et obtenir au moins un 13/15",
          weight: 33.3,
        },
        {
          id: "b-clean-14",
          label:
            "Dans le niveau actuel, jouer une partie sans potion et sans parchemin et obtenir au moins un 14/15",
          weight: 33.4,
        },
      ],
    },
    {
      id: "mastery",
      variants: [
        { id: "b-mastery-3", label: "Augmenter le seuil de maîtrise (sans parchemin) de +3 points", weight: 40 },
        { id: "b-mastery-4", label: "Augmenter le seuil de maîtrise (sans parchemin) de +4 points", weight: 40 },
        { id: "b-mastery-5", label: "Augmenter le seuil de maîtrise (sans parchemin) de +5 points", weight: 20 },
      ],
    },
    {
      id: "discovery",
      variants: [
        { id: "b-discovery-3", label: "Augmenter le seuil de découverte (sans parchemin) de +3 points", weight: 40 },
        { id: "b-discovery-4", label: "Augmenter le seuil de découverte (sans parchemin) de +4 points", weight: 40 },
        { id: "b-discovery-5", label: "Augmenter le seuil de découverte (sans parchemin) de +5 points", weight: 20 },
      ],
    },
  ],
  C: [
    {
      id: "memory-score",
      variants: [
        { id: "c-memscore-10", label: "Jouer le jeu de mémoire et obtenir au moins un score de 10/15", weight: 45 },
        { id: "c-memscore-11", label: "Jouer le jeu de mémoire et obtenir au moins un score de 11/15", weight: 45 },
        { id: "c-memscore-12", label: "Jouer le jeu de mémoire et obtenir au moins un score de 12/15", weight: 10 },
      ],
    },
    {
      id: "grade-improve",
      variants: [
        { id: "c-grade-3", label: "Améliorer d'au moins 1 cran la gradation de 3 mots", weight: 40 },
        { id: "c-grade-5", label: "Améliorer d'au moins 1 cran la gradation de 5 mots", weight: 40 },
        { id: "c-grade-7", label: "Améliorer d'au moins 1 cran la gradation de 7 mots", weight: 20 },
      ],
    },
    {
      id: "perfect-partie",
      partieScore: true,
      variants: [
        { id: "c-perfect-15", label: "Faire un sans-faute dans une partie (obtenir un 15/15)", weight: 100 },
      ],
    },
    {
      id: "common-gem",
      dynamic: true,
      variants: [{ id: "c-common-gem", weight: 100 }],
    },
  ],
  D: [
    {
      id: "training",
      variants: [
        {
          id: "d-train-1",
          label: "Compléter 1 session d'entraînement",
          weight: 1,
        },
        {
          id: "d-train-2",
          label: "Compléter 2 sessions d'entraînement",
          weight: 1,
        },
        {
          id: "d-train-3",
          label: "Compléter 3 sessions d'entraînement",
          weight: 1,
        },
      ],
    },
    {
      id: "test-next-level",
      variants: [
        {
          id: "d-t-next-1",
          label: "En mode T, jouer 1 partie de vocabulaire de niveau supérieur",
          weight: 1,
        },
        {
          id: "d-t-next-2",
          label: "En mode T, jouer 2 parties de vocabulaire de niveau supérieur",
          weight: 1,
        },
        {
          id: "d-t-next-3",
          label: "En mode T, jouer 3 parties de vocabulaire de niveau supérieur",
          weight: 1,
        },
      ],
    },
  ],
};

function createRng(randomFn = Math.random) {
  return {
    next: () => randomFn(),
    int: (max) => Math.floor(randomFn() * max),
    pick: (items) => items[Math.floor(randomFn() * items.length)],
  };
}

function weightedPick(items, getWeight, rng) {
  if (!items.length) return null;
  const total = items.reduce((sum, item) => sum + getWeight(item), 0);
  if (total <= 0) return null;

  let roll = rng.next() * total;
  for (const item of items) {
    roll -= getWeight(item);
    if (roll <= 0) return item;
  }
  return items[items.length - 1];
}

function pickConfiguration(rng) {
  return weightedPick(DAILY_QUEST_CONFIGURATIONS, (config) => config.weight, rng);
}

function buildGroupSlots(config) {
  const slots = [];
  for (let index = 0; index < config.A; index += 1) slots.push("A");
  for (let index = 0; index < config.B; index += 1) slots.push("B");
  for (let index = 0; index < config.C; index += 1) slots.push("C");
  const dCount = Number(config.D);
  const trainingSlots = Number.isFinite(dCount) && dCount > 0 ? dCount : 1;
  for (let index = 0; index < trainingSlots; index += 1) slots.push("D");
  return slots;
}

function getAvailableCategories(group, excludeCategoryIds, excludePartieScore) {
  return QUEST_CATALOG[group].filter((category) => {
    if (excludeCategoryIds.has(category.id)) return false;
    if (excludePartieScore && category.partieScore) return false;
    return category.variants.length > 0;
  });
}

const COMMON_GEM_QUEST_PICK_COUNT = 3;
const COMMON_GEM_ID_SEPARATOR = "+";

function gemLabelForQuest(gemId) {
  return humanizeGemLabel(`${gemId}.png`).toLocaleLowerCase("fr-FR");
}

function pickUniqueCommonGemIds(rng, count = COMMON_GEM_QUEST_PICK_COUNT) {
  const pool = COMMON_GEM_IDS.slice();
  const picked = [];
  const n = Math.min(count, pool.length);
  for (let index = 0; index < n; index += 1) {
    const gemId = rng.pick(pool);
    if (!gemId) break;
    picked.push(gemId);
    const poolIndex = pool.indexOf(gemId);
    if (poolIndex >= 0) pool.splice(poolIndex, 1);
  }
  return picked.sort((left, right) =>
    gemLabelForQuest(left).localeCompare(gemLabelForQuest(right), "fr-FR")
  );
}

function formatCommonGemQuestLabel(gemIds) {
  const labels = gemIds.map(gemLabelForQuest);
  if (labels.length <= 1) {
    const gemLabel = labels[0] || "pierre";
    return `Obtenir une pierre commune : ${gemLabel} (sans passer par la bourse des pierres précieuses)`;
  }

  const list =
    labels.length === 2
      ? `${labels[0]} et ${labels[1]}`
      : `${labels.slice(0, -1).join(", ")} et ${labels[labels.length - 1]}`;
  return `Obtenir une pierre commune parmi : ${list} (sans passer par la bourse des pierres précieuses)`;
}

function resolveCommonGemQuest(variant, rng) {
  const gemIds = pickUniqueCommonGemIds(rng);
  return {
    id: `${variant.id}-${gemIds.join(COMMON_GEM_ID_SEPARATOR)}`,
    statId: variant.id,
    group: "C",
    label: formatCommonGemQuestLabel(gemIds),
    categoryId: "common-gem",
    partieScore: false,
    gemIds,
  };
}

function pickQuestFromGroup(group, excludeCategoryIds, excludePartieScore, rng) {
  const categories = getAvailableCategories(group, excludeCategoryIds, excludePartieScore);
  if (!categories.length) return null;

  const category = rng.pick(categories);
  const variant = weightedPick(category.variants, (entry) => entry.weight, rng);
  if (!variant) return null;

  if (category.dynamic && category.id === "common-gem") {
    return resolveCommonGemQuest(variant, rng);
  }

  return {
    id: variant.id,
    statId: variant.id,
    group,
    label: variant.label,
    categoryId: category.id,
    partieScore: Boolean(category.partieScore),
  };
}

function pickReadingQuest(rng) {
  const readingCategory = QUEST_CATALOG.A.find((category) => category.id === "reading");
  const variant = weightedPick(readingCategory.variants, (entry) => entry.weight, rng);
  if (!variant) return null;
  return {
    id: variant.id,
    statId: variant.id,
    group: "A",
    label: variant.label,
    categoryId: "reading",
    partieScore: false,
  };
}

function applyReadingPity(quests, rng) {
  if (quests.some((quest) => quest.categoryId === "reading")) {
    return quests;
  }

  const aIndexes = quests
    .map((quest, index) => (quest.group === "A" ? index : -1))
    .filter((index) => index >= 0);
  if (!aIndexes.length) return quests;

  const readingQuest = pickReadingQuest(rng);
  if (!readingQuest) return quests;

  const next = quests.slice();
  next[rng.pick(aIndexes)] = readingQuest;
  return next;
}

/**
 * Génère les 4 objectifs du jour (A/B/C + toujours un D).
 * @param {() => number | { randomFn?: () => number, forceReading?: boolean, maxUnlockedLevel?: number }} [randomFn]
 * @param {{ forceReading?: boolean, randomFn?: () => number, maxUnlockedLevel?: number }} [options]
 * @returns {{ config: object, quests: Array<{ id: string, group: string, label: string }> }}
 */
export function generateDailyQuests(randomFn = Math.random, options = {}) {
  if (randomFn && typeof randomFn === "object") {
    options = randomFn;
    randomFn = options.randomFn || Math.random;
  }

  const rng = createRng(randomFn);
  const config = pickConfiguration(rng);
  const slots = buildGroupSlots(config);
  const usedCategoriesByGroup = {
    A: new Set(),
    B: new Set(),
    C: new Set(),
    D: new Set(),
  };
  let excludePartieScore = false;
  if (Number(options.maxUnlockedLevel) >= GAME_LEVEL_COUNT) {
    usedCategoriesByGroup.D.add("test-next-level");
  }
  const quests = [];

  for (const group of slots) {
    let quest = null;
    for (let attempt = 0; attempt < 48; attempt += 1) {
      quest = pickQuestFromGroup(
        group,
        usedCategoriesByGroup[group],
        excludePartieScore,
        rng
      );
      if (quest && !usedCategoriesByGroup[group].has(quest.categoryId)) break;
      quest = null;
    }

    if (!quest) {
      throw new Error(`Impossible de tirer un objectif du groupe ${group}.`);
    }

    usedCategoriesByGroup[group].add(quest.categoryId);
    if (quest.partieScore) {
      excludePartieScore = true;
    }
    quests.push(quest);
  }

  return {
    config,
    quests: options.forceReading ? applyReadingPity(quests, rng) : quests,
  };
}

function buildQuestObjectiveLabelMap() {
  const map = new Map();
  for (const categories of Object.values(QUEST_CATALOG)) {
    for (const category of categories) {
      for (const variant of category.variants) {
        if (variant.label) {
          map.set(variant.id, variant.label);
        }
      }
    }
  }
  return map;
}

const QUEST_OBJECTIVE_LABELS_BY_STAT_ID = buildQuestObjectiveLabelMap();

const NEW_WORDS_DISCOVERY_TARGETS = new Set([10, 15, 20]);

function resolvePhaseQuestObjectiveLabel(statId) {
  const id = String(statId || "");
  const match = id.match(/^(b-unique|b-discovery|c-best)-(\d+)$/);
  if (!match) return "";
  const kind = match[1];
  const target = Number(match[2]);
  if (kind === "b-unique") {
    return `Dans le jeu de vocabulaire, restituer correctement ${target} mots différents dans la journée`;
  }
  if (kind === "b-discovery" && NEW_WORDS_DISCOVERY_TARGETS.has(target)) {
    return `Dans le jeu de vocabulaire, découvrir ${target} nouveaux mots (sans parchemin)`;
  }
  if (kind === "c-best") {
    return `Obtenir un score au moins égal à ${target}/15 dans une partie`;
  }
  return "";
}

function patchLegacyQuestObjectiveLabel(label) {
  if (!label) return "";
  return label
    .replace(/\bUtiliser la clé\b/gi, "Utiliser le parchemin")
    .replace(/\bsans potion et sans clé\b/gi, "sans potion et sans parchemin")
    .replace(/\(sans clé\)/gi, "(sans parchemin)")
    .replace(/\bsans clé\b/gi, "sans parchemin");
}

const COMMON_GEM_BOURSE_NOTE_RE =
  /\s*\(\s*sans passer par la bourse des pierres précieuses\s*\)\s*/i;

/** Retire la précision « bourse » du libellé : elle s'affiche dans une infobulle. */
export function splitCommonGemBourseNote(label) {
  const text = String(label || "");
  if (!COMMON_GEM_BOURSE_NOTE_RE.test(text)) {
    return { text, note: "" };
  }
  return {
    text: text.replace(COMMON_GEM_BOURSE_NOTE_RE, " ").replace(/\s+/g, " ").trim(),
    note: "Sans passer par la bourse des pierres précieuses.",
  };
}

/** Libellé affichable pour un objectif (catalogue courant ou repli sur le texte stocké). */
export function resolveQuestObjectiveLabel(objective) {
  if (!objective) return "";
  if (objective.categoryId === "common-gem") {
    return objective.label || "";
  }
  const statId = objective.statId || objective.id;
  if (statId && QUEST_OBJECTIVE_LABELS_BY_STAT_ID.has(statId)) {
    return QUEST_OBJECTIVE_LABELS_BY_STAT_ID.get(statId);
  }
  const phaseLabel = resolvePhaseQuestObjectiveLabel(statId);
  if (phaseLabel) return phaseLabel;
  return patchLegacyQuestObjectiveLabel(objective.label || "");
}
